import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Globe, Zap, FileText, Mail, History, ArrowRight, CheckCircle, Target, BarChart3, Sparkles, Share2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const navigate = useNavigate();
  const [singleUrl, setSingleUrl] = useState("");
  const [batchUrls, setBatchUrls] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("single");
  const [enhancedMode, setEnhancedMode] = useState(true);

  const handleAnalyzeSingle = async () => {
    if (!singleUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setLoading(true);
    try {
      let response;
      if (enhancedMode) {
        // Use enhanced 360° analysis
        response = await axios.post(`${API}/analyze/enhanced`, { 
          url: singleUrl.trim(),
          include_linkedin: true,
          include_youtube: true,
          include_ad_detection: true
        });
        toast.success("Enhanced 360° analysis started!");
      } else {
        response = await axios.post(`${API}/analyze`, { url: singleUrl.trim() });
        toast.success("Analysis started!");
      }
      navigate(`/jobs/${response.data.job_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start analysis");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeBatch = async () => {
    const urls = batchUrls
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urls.length === 0) {
      toast.error("Please enter at least one URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyze/batch`, { urls });
      toast.success(`Batch analysis started for ${urls.length} URLs!`);
      navigate(`/jobs/${response.data.job_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start batch analysis");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#10b981]"></span>
              <span className="text-sm font-medium">AI-Powered GTM Analysis</span>
            </div>
            <h1 className="font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl" style={{ lineHeight: 1.2 }} data-testid="hero-title">
              Revenue Acceleration Score
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-200 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Analyze any website, score 25 GTM categories across 5 pillars, and auto-generate branded outreach assets in minutes.
            </p>

            {/* URL Input Section */}
            <div className="mt-10 max-w-2xl mx-auto">
              <Tabs defaultValue="single" onValueChange={setMode} className="w-full">
                <TabsList className="bg-white/10 border-none mb-4">
                  <TabsTrigger value="single" className="data-[state=active]:bg-white data-[state=active]:text-brand-ink" data-testid="input-mode-single-tab">
                    <Globe className="w-4 h-4 mr-2" />
                    Single URL
                  </TabsTrigger>
                  <TabsTrigger value="batch" className="data-[state=active]:bg-white data-[state=active]:text-brand-ink" data-testid="input-mode-batch-tab">
                    <Zap className="w-4 h-4 mr-2" />
                    Batch
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="single">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      placeholder="https://company.com"
                      value={singleUrl}
                      onChange={(e) => setSingleUrl(e.target.value)}
                      className="flex-1 h-12 bg-white text-slate-900 border-0"
                      data-testid="single-url-input"
                      onKeyDown={(e) => e.key === "Enter" && handleAnalyzeSingle()}
                    />
                    <Button
                      onClick={handleAnalyzeSingle}
                      disabled={loading}
                      className="h-12 px-8 btn-primary"
                      data-testid="analyze-single-button"
                    >
                      {loading ? "Analyzing..." : enhancedMode ? "360° Analyze" : "Analyze"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  
                  {/* Enhanced Mode Toggle */}
                  <div className="mt-4 flex items-center justify-center gap-3 bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={enhancedMode}
                        onCheckedChange={setEnhancedMode}
                        data-testid="enhanced-mode-toggle"
                      />
                      <span className="text-sm font-medium text-white flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        360° Enhanced Analysis
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {enhancedMode ? "Includes LinkedIn, social presence & ad tracking" : "Website-only analysis"}
                    </span>
                  </div>
                </TabsContent>

                <TabsContent value="batch">
                  <Textarea
                    rows={4}
                    placeholder="Enter one URL per line:&#10;https://company1.com&#10;https://company2.com&#10;https://company3.com"
                    value={batchUrls}
                    onChange={(e) => setBatchUrls(e.target.value)}
                    className="bg-white text-slate-900 border-0 mb-3"
                    data-testid="batch-textarea"
                  />
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={handleAnalyzeBatch}
                      disabled={loading}
                      className="h-12 px-8 btn-primary"
                      data-testid="analyze-batch-button"
                    >
                      {loading ? "Starting..." : "Analyze Batch"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
        <div className="accent-line"></div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-xs md:text-sm font-semibold text-brand uppercase tracking-[0.1em] mb-3">How It Works</h2>
            <p className="text-2xl md:text-3xl font-semibold text-brand-ink">Three steps to actionable insights</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Globe,
                step: "01",
                title: "Deep-Dive Crawl",
                description: "We analyze 20+ pages including About, Products, Pricing, Blog, Case Studies, and more."
              },
              {
                icon: BarChart3,
                step: "02",
                title: "25-Category Scoring",
                description: "AI scores your GTM across 5 pillars: Positioning, Vital Signs, Demand, Sales, and Tech."
              },
              {
                icon: FileText,
                step: "03",
                title: "Branded Deliverable",
                description: "Get a professional report with scores, insights, and a personalized outreach email."
              }
            ].map((item, index) => (
              <Card key={index} className="feature-card p-6 card-hover" data-testid={`how-it-works-card-${index}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-brand-tint flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-brand" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-brand mb-1">STEP {item.step}</div>
                    <h3 className="text-lg font-semibold text-brand-ink mb-2">{item.title}</h3>
                    <p className="text-sm text-[#6b7280]">{item.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-[#f3f4f6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-xs md:text-sm font-semibold text-brand uppercase tracking-[0.1em] mb-3">What You Get</h2>
            <p className="text-2xl md:text-3xl font-semibold text-brand-ink">Everything you need for cold outreach</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Target, title: "100-Point Score", description: "Overall Revenue Acceleration Score with band interpretation" },
              { icon: BarChart3, title: "5 Pillar Breakdown", description: "Detailed analysis of each GTM dimension" },
              { icon: FileText, title: "PDF Deliverable", description: "Branded report ready for client sharing" },
              { icon: Mail, title: "Outreach Email", description: "AI-written personalized cold email" }
            ].map((item, index) => (
              <Card key={index} className="card p-6 text-center card-hover" data-testid={`feature-card-${index}`}>
                <div className="w-12 h-12 rounded-lg bg-brand-tint flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-brand" />
                </div>
                <h3 className="text-base font-semibold text-brand-ink mb-2">{item.title}</h3>
                <p className="text-sm text-[#6b7280]">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 hero-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "25", label: "Categories Analyzed" },
              { value: "5", label: "GTM Pillars" },
              { value: "20+", label: "Pages Crawled" },
              { value: "<5min", label: "Analysis Time" }
            ].map((stat, index) => (
              <div key={index} data-testid={`stat-${index}`}>
                <div className="text-3xl md:text-4xl font-light text-[#10b981]">{stat.value}</div>
                <div className="text-sm text-slate-300 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle className="w-12 h-12 text-[#10b981] mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-semibold text-brand-ink mb-4">Ready to analyze your prospects?</h2>
          <p className="text-[#6b7280] mb-8">Enter a website URL above to get started with your first Revenue Acceleration Score analysis.</p>
          <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="h-12 px-8 btn-primary" data-testid="scroll-to-top-cta">
            Start Analyzing
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0f172a] text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">Revenue Acceleration Score — Powered by AI Integration Hub</p>
        </div>
      </footer>
    </div>
  );
}
